import React, { useState, useRef } from 'react'
import {
  useCompanies,
  useRemoveCompany,
  AddressDTO,
  useGlobalLabels,
} from '@sentecacommerce-theme/lib'
import styled from 'styled-components'
import { Button } from '../../Button'
import { IconPlus, IconClose, IconEdit, Typography } from '../..'
import { AddBillingAddress } from '../AddBillingAddress'
import { Loader } from '../../Loader'

import { getHeight, setHeight } from '../../../Profile/utils/heightPredictor'

export type BillingAddressListProps = {
  isSelectable?: boolean
  selected?: string
  onChange?: (data: any) => void
  withAddNew?: boolean
  labels?: {
    editLabel?: string
    primaryCompanyLabel?: string
    addNewLabel?: string
  }
}

export type EditAddress = {
  isEditing: boolean
  addressData: AddressDTO
}
export const addressDataIntitialState = { createdAt: 0, updatedAt: 0 }

export const BillingAddressList = () => {
  const { data, isLoading } = useCompanies()
  const [isAddingNew, setIsAddingNew] = useState(false)
  const [editThisAddress, setEditThisAddress] = useState({
    isEditing: false,
    addressData: addressDataIntitialState,
  })
  const {
    remove,
    edit,
    address: addressLabels,
    buttonLabels,
  } = useGlobalLabels()
  const [removeBillingAddress, { error }] = useRemoveCompany()
  const openAddNewForm = () => setIsAddingNew(true)
  const closeAddNewForm = () => setIsAddingNew(false)
  const containerRef = useRef<HTMLDivElement | null>(null)

  if (data) setHeight({ ref: containerRef, page: 'billing-addresses' })

  if (isLoading) {
    const height = getHeight('billing-addresses')

    // 46.9rem default height if no billing addresses

    return (
      <LoaderContainer height={height ? height + 'px' : '46.9rem'}>
        <LoaderWrapper>
          <Loader />
        </LoaderWrapper>
      </LoaderContainer>
    )
  }

  if (!data) return null

  const hasAddresses = Boolean(data.length)

  return (
    <Container ref={containerRef}>
      {(!hasAddresses || editThisAddress?.isEditing) && (
        <AddBillingAddress
          hideCloseButton={!editThisAddress?.isEditing}
          onSuccess={() => {}}
          onCancel={() => {
            setEditThisAddress({
              isEditing: false,
              addressData: addressDataIntitialState,
            })
          }}
          editThisAddress={editThisAddress}
          setEditThisAddress={setEditThisAddress}
        />
      )}
      {!isAddingNew &&
        !editThisAddress?.isEditing &&
        data.map((address, index) => (
          <BillingAddressContainer key={`Address__${address._id || index}`}>
            <CompanyInfoContainer>
              <CompanyName>{address?.company?.companyName}</CompanyName>
              <CompanyAddressContainer>
                <Typography>{address?.additionalAddressInfo}</Typography>
                {address?.isDefaultBillingAddress && (
                  <DefaultBillingAddress>
                    {addressLabels?.mainAddress}
                  </DefaultBillingAddress>
                )}
              </CompanyAddressContainer>
              <AdditionalInformation>
                <Typography>{address?.company?.vatId}</Typography>
                <Typography>{address?.company?.companyId}</Typography>
                <Typography>{address?.company?.companyPerson}</Typography>
              </AdditionalInformation>
            </CompanyInfoContainer>
            <IconsContainer>
              <SingleIconContainer
                onClick={() =>
                  setEditThisAddress({
                    isEditing: true,
                    addressData: address,
                  })
                }
              >
                <StyledBtnTitle>{edit}</StyledBtnTitle>
                <IconEdit style={{ width: '1.5rem' }} />
              </SingleIconContainer>
              <SingleIconContainer
                onClick={() => removeBillingAddress({ id: address?._id ?? '' })}
              >
                <StyledBtnTitle>{remove}</StyledBtnTitle>
                <IconClose />
              </SingleIconContainer>
            </IconsContainer>
          </BillingAddressContainer>
        ))}
      {!isAddingNew && hasAddresses && !editThisAddress?.isEditing && (
        <Button
          className="mt-4"
          variant="primary"
          bordered
          small
          type="button"
          onClick={openAddNewForm}
          borderRadius="small"
        >
          <span className="d-flex align-items-center">
            <IconPlus width="1.5rem" className="mr-3" />
            <span>{buttonLabels?.add}</span>
          </span>
        </Button>
      )}
      {isAddingNew && (
        <AddBillingAddress
          onSuccess={closeAddNewForm}
          onCancel={closeAddNewForm}
        />
      )}
    </Container>
  )
}

const Container = styled.div``

const LoaderContainer = styled.div<{ height: string }>`
  /* Change this whenever adding new fields in order for the collapse animation not to break */
  height: ${(props) => props.height};
`

const LoaderWrapper = styled.div.attrs({
  className: 'd-flex align-items-center justify-content-center',
})`
  min-height: 12rem;
`
const SingleIconContainer = styled.div.attrs({
  className: 'd-flex align-items-center justify-content-end',
})`
  position: relative;
  & svg {
    width: 1rem;
    margin-left: 0.5rem;
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.md.min}) {
    width: 100%;
  }
`

const IconsContainer = styled.div.attrs({
  className: 'd-flex align-items-center mt-3 mr-2',
})`
  cursor: pointer;
  justify-content: flex-end;
  ${SingleIconContainer} + ${SingleIconContainer} {
    margin-left: 0.3rem;
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.md.min}) {
    position: absolute;
    flex-direction: column;
    top: 0;
    right: 1.5rem;
    ${SingleIconContainer} + ${SingleIconContainer} {
      margin-top: 1.5rem;
      right: 0.6rem;
      & svg {
        position: relative;
        right: -0.2rem;
      }
    }
  }
`

const BillingAddressContainer = styled.div.attrs({
  className: 'd-flex justify-content-between py-4 px-4 mb-4',
})`
  position: relative;
  flex-direction: column;
  border: 0.1rem solid ${({ theme }) => theme.palette.grey};
  width: 100%;
  @media (min-width: ${({ theme }) => theme.breakpoints.md.min}) {
    flex-direction: row;
  }
`
const StyledBtnTitle = styled(Typography).attrs({ variant: '8' })`
  text-decoration: underline;
`
const CompanyInfoContainer = styled.div.attrs({
  className: 'd-flex align-items-start justify-content-between',
})`
  flex-direction: column;
  padding-bottom: 2.5rem;
  @media (min-width: ${({ theme }) => theme.breakpoints.md.min}) {
    flex-direction: row;
    padding-bottom: 0;
    width: 60%;
  }
`

const CompanyName = styled(Typography).attrs({ className: '' })`
  @media (min-width: ${({ theme }) => theme.breakpoints.md.min}) {
    width: 33.333%;
  }
`

const CompanyAddressContainer = styled.div.attrs({
  className: 'd-flex flex-column justify-content-between',
})`
  height: 100%;
  ${Typography} {
    margin-top: 0.5rem;
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.md.min}) {
    width: 33.333%;
    ${Typography} {
      margin-top: 0;
    }
  }
`

const AdditionalInformation = styled.div.attrs({
  className: 'd-flex flex-column align-items-start',
})`
  margin-top: 1rem;
  ${Typography} {
    margin-top: 0.5rem;
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.md.min}) {
    width: 33.333%;
    margin-top: 0;
    ${Typography} {
      margin-top: 0;
    }
    ${Typography} + ${Typography} {
      margin-top: 0.5rem;
    }
  }
`

const DefaultBillingAddress = styled(Typography)`
  position: absolute;
  bottom: 3.7rem;
  font-weight: 500;
  @media (min-width: ${({ theme }) => theme.breakpoints.md.min}) {
    position: relative;
    bottom: 0;
  }
`
